import { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Grid, Skeleton, useTheme, alpha, Paper } from '@mui/material';
import { DatePicker, Select, Typography } from '@sm-dashboard/ui-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { leadsUrl } from '@/constants';
import { getUser } from '@/util/getUser';
import { useAppContext } from '@/context/context';
import CrmMetricCard from '@/components/dashboard/crm/CRM-Dashboard/CrmMetricCard';
import MessageIcon from '@mui/icons-material/Message';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import PeopleIcon from '@mui/icons-material/People';
import MapIcon from '@mui/icons-material/Map';
import RecentActivitiesList from '@/components/dashboard/crm/dashboard/RecentActivitiesList';
import { HeatmapMapContainer } from '@/components/dashboard/crm/heatmap/HeatmapMapContainer';
import { useHeatmapData } from '@/components/dashboard/crm/dashboard/useHeatmapData';
import MyEventsList from '@/components/dashboard/crm/CRM-Dashboard/MyEventsList';
import DonationsCampaignCard from '@/components/dashboard/crm/CRM-Dashboard/DonationsCampaignCard';
import TasksCard from '@/components/dashboard/crm/CRM-Dashboard/TasksCard';
import UpcomingDatesCard from '@/components/dashboard/crm/CRM-Dashboard/UpcomingDatesCard';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';
import { useAnalyticsData } from '@/components/dashboard/shared/analytics/useAnalyticsData';
import { MOBILE_BOTTOM_NAV_INSET } from '@/layouts/mobileLayoutConstants';

const CRM_CONTACTS_URL = 'pvt/crm/contacts';
const CRM_CONTACTS_ALL_URL = 'pvt/crm/contacts/all';

export enum TimeRange {
  '1week' = '1week',
  '30days' = '30days',
  'custom' = 'custom'
}

interface CrmDashboardProps {
  onReady?: () => void;
}

const CrmDashboard = ({ onReady }: CrmDashboardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userPermission } = useAppContext();
  const { data: leadsData, loading: leadsLoading, fetchData: fetchLeads } = useFetch();
  const { data: contactsData, loading: contactsLoading, fetchData: fetchContacts } = useFetch();
  const { data: allContactsData, loading: allContactsLoading, fetchData: fetchAllContacts } = useFetch();
  const user = getUser();
  const [timeRange, setTimeRange] = useState<TimeRange>(TimeRange['30days']);
  const [customStartDate, setCustomStartDate] = useState<string | null>(null);
  const [customEndDate, setCustomEndDate] = useState<string | null>(null);
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [tempCustomStartDate, setTempCustomStartDate] = useState<string | null>(null);
  const [tempCustomEndDate, setTempCustomEndDate] = useState<string | null>(null);
  const { heatmapData, loading: heatmapLoading, error: heatmapError } = useHeatmapData();

  const hasAccess =
    userPermission?.isAdmin || userPermission?.hasTag('CRM') || userPermission?.hasPagePermission('/crm/dashboard');

  const dateRange = useMemo(() => {
    const currentDate = new Date();
    let startDate: Date;
    let endDate: Date = currentDate;

    if (timeRange === TimeRange['custom']) {
      if (customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
      } else {
        startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - 30);
      }
    } else {
      switch (timeRange) {
        case TimeRange['1week']:
          startDate = new Date(currentDate);
          startDate.setDate(currentDate.getDate() - 7);
          break;
        case TimeRange['30days']:
          startDate = new Date(currentDate);
          startDate.setDate(currentDate.getDate() - 30);
          break;
        default:
          startDate = new Date(currentDate);
          startDate.setDate(currentDate.getDate() - 30);
      }
    }

    const formatDateOnly = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDateOnly(startDate),
      endDate: formatDateOnly(endDate)
    };
  }, [timeRange, customStartDate, customEndDate]);

  useEffect(() => {
    if (hasAccess && user?.id) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('startDate', dateRange.startDate);
      newParams.set('endDate', dateRange.endDate);
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, hasAccess, user?.id]);

  const buildQueryParams = () => {
    const queryParams = new URLSearchParams({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: '0',
      rowsPerPage: '1000',
      shaliachId: String(user?.id)
    });
    if (userPermission?.operations?.length) {
      queryParams.append('operationsAllowed', userPermission.operations.join(','));
      queryParams.append('shaliachIdAllow', String(user?.id));
    }
    return queryParams;
  };

  useEffect(() => {
    if (hasAccess && user?.id) {
      const queryParams = buildQueryParams();

      fetchLeads({
        method: 'GET',
        url: `${leadsUrl}?${queryParams.toString()}`
      });
      const contactsQueryParams = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: '0',
        rowsPerPage: '1000',
        shaliachId: String(user.id)
      });

      fetchContacts({
        method: 'GET',
        url: `${CRM_CONTACTS_URL}?${contactsQueryParams.toString()}`
      });
      try {
        fetchAllContacts({
          method: 'POST',
          url: `${CRM_CONTACTS_ALL_URL}`,
          data: {}
        });
      } catch (e) {
        console.error('Error fetching all contacts:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess, dateRange.startDate, dateRange.endDate, user?.id, userPermission?.operations]);

  const { analyticsData: purchasesAnalytics, loading: purchasesAnalyticsLoading } = useAnalyticsData({
    type: 'purchases',
    queryParams: buildQueryParams()
  });
  const { analyticsData: donationsAnalytics, loading: donationsAnalyticsLoading } = useAnalyticsData({
    type: 'donations',
    queryParams: buildQueryParams()
  });

  const loading =
    donationsAnalyticsLoading || purchasesAnalyticsLoading || leadsLoading || contactsLoading || allContactsLoading;

  const hasNotifiedReady = useRef(false);

  useEffect(() => {
    if (!onReady || hasNotifiedReady.current || !hasAccess) {
      return;
    }

    if (!loading) {
      hasNotifiedReady.current = true;
      setTimeout(() => {
        onReady();
      }, 300);
    }
  }, [
    onReady,
    hasAccess,
    donationsAnalyticsLoading,
    purchasesAnalyticsLoading,
    leadsLoading,
    contactsLoading,
    loading
  ]);

  // Map contacts (integer id) to UpcomingDatesCard format (_id string)
  const upcomingDatesContacts = useMemo(() => {
    const rawContacts = allContactsData?.rows || contactsData?.rows || [];
    return rawContacts.map((contact: any) => ({
      ...contact,
      _id: contact._id || String(contact.id),
      dynamicDates: contact.dynamicDates ?? contact.CrmContactDynamicDates ?? []
    }));
  }, [allContactsData?.rows, contactsData?.rows]);

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%'
        }}
      >
        <Box sx={{ color: 'error.main' }}>
          <Typography variant="h6">אין לך הרשאות גישה לדף זה</Typography>
        </Box>
      </Box>
    );
  }

  const navigateToDeskGrid = (path: string) => {
    const params = buildQueryParams();
    params.set('page', '0');
    params.set('rowsPerPage', '100');
    navigate(`${path}?${params.toString()}`);
  };

  const donationsAmount = donationsAnalytics?.totalAmount || 0;
  const salesAmount = purchasesAnalytics?.totalAmount || 0;
  const leadsCount = leadsData?.count || 0;
  const contactsCount = contactsData?.count || 0;

  const donationsCreditedAfterFeeSum = donationsAnalytics?.pendingCreditNet || 0;
  const purchasesCreditedAfterFeeSum = purchasesAnalytics?.pendingCreditNet || 0;

  return (
    <Box
      sx={{
        width: '100%',
        p: { xs: 2, md: 3 },
        // Keep the last cards clear of the floating mobile bottom nav.
        pb: { xs: `calc(${MOBILE_BOTTOM_NAV_INSET} + 16px)`, md: 3 },
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ color: 'text.secondary' }}>
            <Typography variant="body">הנתונים שלך</Typography>
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Select
              value={timeRange}
              onChange={value => {
                const newValue = value as TimeRange;
                if (newValue === TimeRange['custom']) {
                  setTempCustomStartDate(customStartDate);
                  setTempCustomEndDate(customEndDate);
                  setDateRangeDialogOpen(true);
                } else {
                  setTimeRange(newValue);
                }
              }}
              options={[
                { value: TimeRange['1week'], label: 'שבוע אחרון' },
                { value: TimeRange['30days'], label: 'חודש אחרון' },
                { value: TimeRange['custom'], label: 'בחירת טווח תאריכים' }
              ]}
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 1, md: 3 }} sx={{ width: '100%', mt: 0 }}>
        <Grid item xs={3} md={6} lg={3} sx={{ display: 'flex', minWidth: 0, width: { lg: '25%' } }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{ borderRadius: 1, width: '100%', height: { xs: 100, md: 150 } }}
            />
          ) : (
            <CrmMetricCard
              icon={<PeopleIcon />}
              label="אנשי קשר חדשים"
              value={contactsCount}
              bgColor={alpha(theme.palette.secondary.main, 0.2)}
              onClick={() => navigate('/crm/contacts')}
              tooltip="למעבר לטבלת אנשי קשר"
            />
          )}
        </Grid>

        <Grid item xs={3} md={6} lg={3} sx={{ display: 'flex', minWidth: 0, width: { lg: '25%' } }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{ borderRadius: 1, width: '100%', height: { xs: 100, md: 150 } }}
            />
          ) : (
            <CrmMetricCard
              icon={<VolunteerActivismIcon />}
              label="תרומות"
              value={`${donationsAmount.toLocaleString('he-IL')} ₪`}
              bgColor={alpha(theme.palette.success.main, 0.2)}
              secondaryText={`יתרה לזיכוי ${donationsCreditedAfterFeeSum.toLocaleString('he-IL')} ₪`}
              onClick={() => navigateToDeskGrid('/desk/donations')}
              tooltip="למעבר לטבלת תרומות"
            />
          )}
        </Grid>

        <Grid item xs={3} md={6} lg={3} sx={{ display: 'flex', minWidth: 0, width: { lg: '25%' } }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{ borderRadius: 1, width: '100%', height: { xs: 100, md: 150 } }}
            />
          ) : (
            <CrmMetricCard
              icon={<ShoppingCartIcon />}
              label="מכירות"
              value={`${salesAmount.toLocaleString('he-IL')} ₪`}
              bgColor={alpha(theme.palette.error.main, 0.2)}
              secondaryText={`יתרה לזיכוי ${purchasesCreditedAfterFeeSum.toLocaleString('he-IL')} ₪`}
              onClick={() => navigateToDeskGrid('/desk/purchases')}
              tooltip="למעבר לטבלת מכירות"
            />
          )}
        </Grid>

        <Grid item xs={3} md={6} lg={3} sx={{ display: 'flex', minWidth: 0, width: { lg: '25%' } }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{ borderRadius: 1, width: '100%', height: { xs: 100, md: 150 } }}
            />
          ) : (
            <CrmMetricCard
              icon={<MessageIcon />}
              label="פניות"
              value={leadsCount}
              bgColor={alpha(theme.palette.info.main, 0.2)}
              onClick={() => navigateToDeskGrid('/desk/leads')}
              tooltip="למעבר לטבלת פניות"
            />
          )}
        </Grid>
      </Grid>

      {/* Activities and Heatmap Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4.5} sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
          <RecentActivitiesList />
        </Grid>
        <Grid item xs={12} md={7.5} sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'common.white',
                p: 1,
                pl: 2,
                borderRadius: '30px 30px 0 0',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexDirection: 'row'
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <MapIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography variant="h6">מפה</Typography>
              </Box>
            </Box>
            <Paper
              sx={{
                borderRadius: '0 0 20px 20px',
                overflow: 'hidden',
                p: 2,
                bgcolor: theme.palette.background.paper,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 320,
                height: 320
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'white'
                }}
              >
                <HeatmapMapContainer
                  contacts={heatmapData as any}
                  mode="location"
                  loading={heatmapLoading}
                  error={heatmapError}
                />
              </Box>
            </Paper>
          </Box>
          <MyEventsList />
          <DonationsCampaignCard />
        </Grid>
      </Grid>

      {/* Additional Cards Section */}
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <TasksCard />
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <UpcomingDatesCard contacts={upcomingDatesContacts} />
        </Grid>
      </Grid>

      {/* Date Range Dialog */}
      <DynamicDialog
        open={dateRangeDialogOpen}
        title="בחירת טווח תאריכים"
        subtitle=""
        handleNotOkClose={() => {
          setDateRangeDialogOpen(false);
          setTempCustomStartDate(customStartDate);
          setTempCustomEndDate(customEndDate);
        }}
        handleOkClose={() => {
          if (tempCustomStartDate && tempCustomEndDate) {
            setCustomStartDate(tempCustomStartDate);
            setCustomEndDate(tempCustomEndDate);
            setTimeRange(TimeRange['custom']);
          }
          setDateRangeDialogOpen(false);
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mt: 2,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            overflow: 'hidden',
            '& .MuiFormControl-root, & .MuiTextField-root': {
              width: '100%',
              maxWidth: '100%'
            }
          }}
        >
          <DatePicker
            appearance="plain"
            label="תאריך התחלה"
            value={tempCustomStartDate || ''}
            onChange={setTempCustomStartDate}
          />
          <DatePicker
            appearance="plain"
            label="תאריך סיום"
            value={tempCustomEndDate || ''}
            onChange={setTempCustomEndDate}
          />
        </Box>
      </DynamicDialog>
    </Box>
  );
};

export default CrmDashboard;
